import { useFormContext } from 'react-hook-form';
import React, { useEffect, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Typography, Button, Box, Stack } from '@mui/material';
import { MFA_SMS_COOLDOWN_MS, ONE_SECOND_MS } from '@shared/auth/constants';

import { SetupPage } from '../SetupPage';
import { useAuth } from '../../../../hooks';
import { NUMBER_OF_OTP_INPUTS, OTPInput, parseOTPInputValue } from '../../components/OTPInput';
import { mfaColors, mfaPrimaryButtonStyles, mfaErrorStyles, mfaOTPContainerStyles } from '../styles';

const getResendButtonLabel = ({ isCoolingDown, remainingCooldown, isSendingCode }) => {
  if (isCoolingDown) {
    return `Resend in ${remainingCooldown}s`;
  }

  if (isSendingCode) {
    return 'Sending...';
  }

  return 'Resend code';
};

const ResendCodeButton = ({ lastSent, handleSendVerificationCode, isSubmitting, isSendingCode }) => {
  const [remainingCooldown, setRemainingCooldown] = useState(0);

  const isCoolingDown = remainingCooldown > 0;
  const isDisabled = isCoolingDown || isSubmitting || isSendingCode;
  const resendButtonLabel = getResendButtonLabel({ isCoolingDown, remainingCooldown, isSendingCode });

  useEffect(() => {
    if (lastSent === 0) {
      setRemainingCooldown(0);
      return undefined;
    }

    const updateRemainingCooldown = () => {
      setRemainingCooldown(Math.max(0, Math.ceil((MFA_SMS_COOLDOWN_MS - (Date.now() - lastSent)) / ONE_SECOND_MS)));
    };

    updateRemainingCooldown();
    const interval = setInterval(updateRemainingCooldown, ONE_SECOND_MS);

    return () => clearInterval(interval);
  }, [lastSent]);

  return (
    <Button
      variant="text"
      onClick={handleSendVerificationCode}
      disabled={isDisabled}
      startIcon={<RefreshIcon sx={{ fontSize: 18 }} />}
      sx={{
        color: isDisabled ? mfaColors.textMuted : mfaColors.primary,
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.875rem',
        '&:hover': { backgroundColor: mfaColors.background },
        '&.Mui-disabled': { color: mfaColors.textMuted },
      }}
    >
      {resendButtonLabel}
    </Button>
  );
};

const VerificationError = ({ message }) => {
  if (!message) {
    return null;
  }

  return (
    <Box sx={mfaErrorStyles}>
      <ErrorOutlineIcon sx={{ color: mfaColors.error, fontSize: 18, mt: 0.25 }} />
      <Typography variant="body2" sx={{ color: mfaColors.error }}>
        {message}
      </Typography>
    </Box>
  );
};

export const VerifyCodeSection = ({
  verificationId,
  onSubmit,
  lastSent,
  handleSendVerificationCode,
  isSendingCode,
}) => {
  const { user } = useAuth();
  /** @type {import('react-hook-form').UseFormReturn<SMSFormType>} */
  const form = useFormContext();
  const { formState } = form;
  const { isSubmitting, errors } = formState;

  if (!verificationId || !user) {
    return null;
  }

  const code = form.watch('verificationCode');
  const errorMessage = errors.verificationCode?.message || errors.root?.message;
  const hasError = !!errorMessage;

  return (
    <Stack spacing={3} sx={{ width: '100%' }}>
      <Typography variant="body2" sx={{ color: mfaColors.textMuted, textAlign: 'center' }}>
        Enter the {NUMBER_OF_OTP_INPUTS}-digit code we sent to your phone
      </Typography>

      <Box sx={{ ...mfaOTPContainerStyles, borderColor: hasError ? mfaColors.error : mfaColors.border }}>
        <OTPInput onLastInput={form.handleSubmit(onSubmit)} />
      </Box>

      <VerificationError message={errorMessage} />

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <ResendCodeButton
          lastSent={lastSent}
          isSubmitting={isSubmitting}
          isSendingCode={isSendingCode}
          handleSendVerificationCode={form.handleSubmit(handleSendVerificationCode)}
        />
      </Box>

      <Stack direction="row" spacing={2} justifyContent="center">
        <SetupPage.CancelButton disabled={isSubmitting} user={user} />
        <Button
          type="submit"
          disabled={parseOTPInputValue(code).length !== NUMBER_OF_OTP_INPUTS || isSubmitting}
          sx={mfaPrimaryButtonStyles}
        >
          {isSubmitting ? 'Verifying...' : 'Verify and Enable'}
        </Button>
      </Stack>
    </Stack>
  );
};
